import {
  Button,
  Card,
  Form,
  InputNumber,
  message,
  Select,
  Space,
  TimePicker,
} from 'antd';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useSWRConfig } from 'swr';

import {
  asyncErrorHandler,
  type ErrorResponse,
} from '@/utils/asyncErrorHandler';

import AddOrEditCleanKind from '../../AddOrEditCleanKind';
import { timeUnits } from '../../helper';
import sharedStyles from '../../sharedStyles.module.scss';
import type { ReservationRuleFormValues } from '../../sharedTypes';
import {
  canReserveValidator,
  formatCleanKindRulesToRequest,
  getTypeCheckedCleanKindRules,
} from './helper';
import styles from './index.module.scss';

type CleanKindManagementRulesProps = {
  getLatestData: () => void;
  cleanKindRules: ReservationRuleFormValues | undefined;
};

const { Item, useForm } = Form;

const CleanKindManagementRules = ({
  getLatestData,
  cleanKindRules,
}: CleanKindManagementRulesProps) => {
  const { mutate } = useSWRConfig();

  const [form] = useForm<ReservationRuleFormValues>();
  const [isEditingRules, setIsEditingRules] = useState(false);
  const [isCanReserveRangeError, setIsCanReserveRangeError] = useState(false);

  useEffect(() => {
    if (cleanKindRules) {
      const typeCheckedCleanKindRules =
        getTypeCheckedCleanKindRules(cleanKindRules);
      form.setFieldsValue(typeCheckedCleanKindRules);
    }
  }, [cleanKindRules, form]);

  const showErrorMsg = (errorMsg: string) => {
    message.error(errorMsg);
    setIsCanReserveRangeError(true);
  };

  const handleRuleFormSubmit = async (
    formValues: ReservationRuleFormValues
  ) => {
    const isCanReserveRangeValidated = canReserveValidator({
      allValues: formValues,
      showErrorMsg,
    });
    if (!isCanReserveRangeValidated) {
      return;
    }
    let ruleId = '';
    if (cleanKindRules?._id) {
      ruleId = cleanKindRules._id;
    }
    const data = formatCleanKindRulesToRequest(ruleId, formValues);
    try {
      await axios({
        method: 'POST',
        url: '/ezcon/api/editReservationRule',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
        data,
      });
      mutate('/ezcon/api/fetchReservationRule');
      setIsEditingRules(false);
      message.success('編輯清潔服務預約管理辦法成功!');
    } catch (error) {
      const errObj: ErrorResponse = error.response.data;
      asyncErrorHandler(errObj, '編輯清潔服務預約管理辦法失敗!');
    }
  };

  const handleRuleFormValuesChange = (
    changedValues: ReservationRuleFormValues
  ) => {
    const needCheckKeys = [
      'canReserveAfterNumber',
      'canReserveAfterUnit',
      'canReserveBeforeNumber',
      'canReserveBeforeUnit',
    ];
    const key = Object.keys(changedValues)[0];
    const isIncluded = needCheckKeys.includes(key);

    if (isIncluded && isCanReserveRangeError) {
      setIsCanReserveRangeError(false);
    }
  };

  const cancelEditRule = () => {
    form.resetFields();
    setIsEditingRules(false);
  };

  const publishNewRule = async () => {
    try {
      await axios({
        method: 'POST',
        url: '/ezcon/api/publishReservationRule',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
      });
      message.success('發佈公告成功!');
    } catch (error) {
      const errObj: ErrorResponse = error.response.data;
      asyncErrorHandler(errObj, '發佈公告失敗!');
    }
  };

  return (
    <Space direction="vertical" className={sharedStyles.widthFull}>
      <AddOrEditCleanKind type="add" getLatestData={getLatestData} />
      <Card>
        <Space direction="vertical" className={sharedStyles.widthFull}>
          <span className={styles.tableTitle}>清潔服務預約管理辦法</span>
          <Form
            form={form}
            colon={false}
            disabled={isEditingRules === false}
            onFinish={handleRuleFormSubmit}
            onValuesChange={handleRuleFormValuesChange}
            initialValues={
              cleanKindRules
                ? getTypeCheckedCleanKindRules(cleanKindRules)
                : cleanKindRules
            }
          >
            <Item>
              <div className={sharedStyles.flexbox}>
                <div className={sharedStyles.flexbox}>
                  <span>1.預約日期前</span>
                  <Item
                    name="mustCancelBeforeNumber"
                    requiredMark="optional"
                    rules={[{ required: true, message: '請輸入數字' }]}
                  >
                    <InputNumber min={0} className={styles.numberSelector} />
                  </Item>
                  <Item
                    name="mustCancelBeforeUnit"
                    className={styles.unitSelector}
                    requiredMark="optional"
                    rules={[{ required: true, message: '請選擇單位' }]}
                  >
                    <Select options={timeUnits} />
                  </Item>
                </div>
                <span>可取消預約，並全額退費。</span>
              </div>
            </Item>
            <Item>
              <div className={sharedStyles.flexbox}>
                <div className={sharedStyles.flexbox}>
                  <span>2.會員可預約範圍，為預約當日後</span>
                  <Item
                    name="canReserveAfterNumber"
                    requiredMark="optional"
                    validateStatus={
                      isCanReserveRangeError ? 'error' : 'success'
                    }
                  >
                    <InputNumber min={0} className={styles.numberSelector} />
                  </Item>
                  <Item
                    name="canReserveAfterUnit"
                    className={styles.unitSelector}
                    validateStatus={
                      isCanReserveRangeError ? 'error' : 'success'
                    }
                  >
                    <Select options={timeUnits} />
                  </Item>
                  <span>至</span>
                  <Item
                    name="canReserveBeforeNumber"
                    validateStatus={
                      isCanReserveRangeError ? 'error' : 'success'
                    }
                  >
                    <InputNumber min={0} className={styles.numberSelector} />
                  </Item>
                  <Item
                    name="canReserveBeforeUnit"
                    className={styles.unitSelector}
                    validateStatus={
                      isCanReserveRangeError ? 'error' : 'success'
                    }
                  >
                    <Select options={timeUnits} />
                  </Item>
                </div>
                <span>。</span>
              </div>
              <span>
                （尚未開放會員預約之日期，可以透過下方的「每日清潔服務預約次數設定」功能，來編輯預約次數上限。）
              </span>
            </Item>
            <div className={sharedStyles.flexbox}>
              <span>3.預約清潔日當天</span>
              <Item
                name="autoPushNotification"
                requiredMark="optional"
                rules={[{ required: true, message: '請選擇時間' }]}
              >
                <TimePicker inputReadOnly format="HH:mm" />
              </Item>
              <span>自動發送會員推播通知。</span>
            </div>
          </Form>
          <Space className={styles.formFooterContainer}>
            <Button
              type={isEditingRules ? 'default' : 'primary'}
              onClick={isEditingRules ? cancelEditRule : publishNewRule}
            >
              {isEditingRules ? '取消' : '發佈公告'}
            </Button>
            <Button
              type={isEditingRules ? 'primary' : 'default'}
              onClick={() =>
                isEditingRules ? form.submit() : setIsEditingRules(true)
              }
            >
              {isEditingRules ? '完成' : '編輯'}
            </Button>
          </Space>
        </Space>
      </Card>
    </Space>
  );
};

export default CleanKindManagementRules;
